import React, { useEffect, useState, useRef, Children, lazy } from 'react';
import { cn } from '@/lib/utils';
import { motion } from 'framer-motion';
import { CheckCircle2, Star, Heart, ShieldCheck, Clock, ChevronDown, ChevronUp, BookOpen, Gift, ArrowRight, XCircle, Sparkles, Trophy, Headphones } from 'lucide-react';
const BOOK_IMAGE = 'https://cdn.magicpatterns.com/uploads/84YTUZKd9AaE9sJBdshcSr/ChatGPT_Image_23_jun_2026,_10_15_46.png';
const CAROUSEL_IMAGES = [{
  src: 'https://cdn.magicpatterns.com/uploads/sQVSN4zyrkFgYXoMF2Xjsi/Psicologa_dijo_sobre_cama_202606231147.jpg',
  alt: 'Libro Mi Psicóloga Me Dijo sobre la cama'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/tsGsPTQXRvtvarqMDQwZgm/Fotografia_ultra_realista_cinematografica,_libro_202606231145.jpg',
  alt: 'Libro Mi Psicóloga Me Dijo en la playa al atardecer'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/irckEEN32KnF2dzHmkqmM3/Fotograma_ultra_realista_estilo_influencer_202606231145_-_copia.jpg',
  alt: 'Lectora mostrando el libro Mi Psicóloga Me Dijo'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/6PXiVhwGKuieVy1eLi9cZS/Retrato_ultra_realista_de_una_202606231145.jpg',
  alt: 'Mujer abrazando el libro Mi Psicóloga Me Dijo'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/d1shdnpsSPaDJWqZ8XzzfW/Fotografia_ultra_realista_estilo_UGC_202606231145.jpg',
  alt: 'Lectora sosteniendo el libro Mi Psicóloga Me Dijo'
}];
function ImageCarousel() {
  const [index, setIndex] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval>>();
  const total = CAROUSEL_IMAGES.length;
  const goTo = (i: number) => setIndex((i % total + total) % total);
  useEffect(() => {
    timerRef.current = setInterval(() => {
      setIndex(prev => (prev + 1) % total);
    }, 4000);
    return () => clearInterval(timerRef.current);
  }, [total]);
  return <section className="py-10 md:py-16 px-4 bg-brand-light" data-id="element-0">
      <div className="max-w-3xl mx-auto" data-id="element-1">
        <div className="relative overflow-hidden rounded-3xl shadow-xl bg-white aspect-[4/5] md:aspect-[16/10]" data-id="element-2">
          {CAROUSEL_IMAGES.map((img, i) => <img key={img.src} src={img.src} alt={img.alt} loading={i === 0 ? 'eager' : 'lazy'} decoding="async" className={cn('absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ease-in-out', i === index ? 'opacity-100' : 'opacity-0')} data-id="element-3" />)}
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-brand-dark/20 to-transparent" data-id="element-4" />
        </div>
        <div className="flex justify-center gap-2.5 mt-6" data-id="element-5">
          {CAROUSEL_IMAGES.map((_, i) => <button key={i} type="button" onClick={() => goTo(i)} aria-label={`Ver imagen ${i + 1}`} aria-current={i === index} className={cn('h-2.5 rounded-full transition-all', i === index ? 'w-7 bg-brand-terracotta' : 'w-2.5 bg-brand-dark/20 hover:bg-brand-dark/40')} data-id="element-6" />)}
        </div>
      </div>
    </section>;
}
const fadeIn = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6
    }
  }
};
const staggerContainer = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};
function CountdownTimer() {
  const [timeLeft, setTimeLeft] = useState({
    hours: 23,
    minutes: 59,
    seconds: 59
  });
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev.seconds > 0) return {
          ...prev,
          seconds: prev.seconds - 1
        };
        if (prev.minutes > 0) return {
          ...prev,
          minutes: prev.minutes - 1,
          seconds: 59
        };
        if (prev.hours > 0) return {
          ...prev,
          hours: prev.hours - 1,
          minutes: 59,
          seconds: 59
        };
        return prev;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, []);
  return <div className="flex items-center justify-center gap-4 text-brand-terracotta font-bold text-2xl md:text-4xl my-6" data-id="element-7">
      <div className="flex flex-col items-center" data-id="element-8">
        <span className="bg-white px-4 py-2 rounded-lg shadow-sm border border-brand-terracotta/20" data-id="element-9">
          {String(timeLeft.hours).padStart(2, '0')}
        </span>
        <span className="text-xs uppercase tracking-wider mt-2 text-brand-dark/60" data-id="element-10">
          Horas
        </span>
      </div>
      <span className="text-brand-terracotta pb-6" data-id="element-11">:</span>
      <div className="flex flex-col items-center" data-id="element-12">
        <span className="bg-white px-4 py-2 rounded-lg shadow-sm border border-brand-terracotta/20" data-id="element-13">
          {String(timeLeft.minutes).padStart(2, '0')}
        </span>
        <span className="text-xs uppercase tracking-wider mt-2 text-brand-dark/60" data-id="element-14">
          Minutos
        </span>
      </div>
      <span className="text-brand-terracotta pb-6" data-id="element-15">:</span>
      <div className="flex flex-col items-center" data-id="element-16">
        <span className="bg-white px-4 py-2 rounded-lg shadow-sm border border-brand-terracotta/20" data-id="element-17">
          {String(timeLeft.seconds).padStart(2, '0')}
        </span>
        <span className="text-xs uppercase tracking-wider mt-2 text-brand-dark/60" data-id="element-18">
          Segundos
        </span>
      </div>
    </div>;
}
function CTAButton({
  text,
  className = '',
  href = '#oferta'
}: {
  text: string;
  className?: string;
  href?: string;
}) {
  return <a href={href} className={cn(`inline-flex items-center justify-center gap-2 bg-brand-terracotta hover:bg-[#a84a30] text-white font-bold text-lg md:text-xl py-4 px-8 rounded-full transition-all transform hover:scale-105 shadow-lg hover:shadow-xl w-full sm:w-auto ${className}`, 'text-[15px]')} data-id="element-19">
      <Heart className="w-5 h-5" data-id="element-20" />
      {text}
      <ArrowRight className="w-5 h-5" data-id="element-21" />
    </a>;
}
function FAQItem({
  question,
  answer
}: {
  question: string;
  answer: string;
}) {
  const [isOpen, setIsOpen] = useState(false);
  return <div className="border-b border-brand-dark/10 py-4" data-id="element-22">
      <button onClick={() => setIsOpen(!isOpen)} className="flex w-full items-center justify-between text-left font-serif text-lg md:text-xl font-medium text-brand-dark hover:text-brand-purple transition-colors" data-id="element-23">
        {question}
        {isOpen ? <ChevronUp className="w-5 h-5 text-brand-terracotta flex-shrink-0" data-id="element-24" /> : <ChevronDown className="w-5 h-5 text-brand-terracotta flex-shrink-0" data-id="element-25" />}
      </button>
      <motion.div initial={false} animate={{
      height: isOpen ? 'auto' : 0,
      opacity: isOpen ? 1 : 0
    }} className="overflow-hidden" data-id="element-26">
        <p className="pt-4 text-brand-dark/80 leading-relaxed" data-id="element-27">{answer}</p>
      </motion.div>
    </div>;
}
type OfferPack = {
  id: string;
  badge?: string;
  title: string;
  items: string[];
  regularPrice: string;
  price: string;
  highlighted?: boolean;
  checkoutUrl: string;
};
const OFFER_PACKS: OfferPack[] = [{
  id: 'basic',
  title: 'PACK TRANSFORMACIÓN PERSONAL',
  items: ['Libro Digital Completo: Mi Psicóloga Me Dijo™', '4 Bonos Estratégicos incluidos'],
  regularPrice: '$49.999',
  price: '$19.999',
  checkoutUrl: 'https://indigo33.shop/cart/64096314196337:1'
}, {
  id: 'audio',
  badge: 'EL MÁS ELEGIDO',
  title: 'PACK COMPLETO + AUDIO LIBRO',
  items: ['Mi Psicóloga Me Dijo™', '4 Bonos Estratégicos incluidos', 'AUDIO LIBRO DE MI PSICOLOGA ME DIJO'],
  regularPrice: '$69.999',
  price: '$29.999',
  highlighted: true,
  checkoutUrl: 'https://indigo33.shop/cart/64096320749937:1'
}];
function OfferSelector() {
  const [selected, setSelected] = useState('audio');
  const activePack = OFFER_PACKS.find(p => p.id === selected) ?? OFFER_PACKS[1];
  return <div className="text-center" data-id="element-28">
      <h2 className="font-serif text-3xl md:text-4xl font-bold text-brand-dark mb-2" data-id="element-29">
        ELEGÍ TU PACK
      </h2>
      <p className="text-brand-dark/60 font-medium mb-8" data-id="element-30">
        Seleccioná la opción que mejor se adapta a vos.
      </p>

      <div className="space-y-7 mb-10 pt-2 text-left" data-id="element-31">
        {OFFER_PACKS.map(pack => {
        const isSelected = selected === pack.id;
        return <button key={pack.id} type="button" onClick={() => setSelected(pack.id)} aria-pressed={isSelected} className={cn('w-full relative rounded-3xl border-2 p-5 md:p-6 transition-all text-left', pack.badge ? 'pt-7 md:pt-6' : '', isSelected ? 'border-brand-terracotta bg-brand-terracotta/5 shadow-lg' : 'border-brand-dark/10 bg-white hover:border-brand-terracotta/40')} data-id="element-32">
              {pack.badge && <div className="absolute -top-3 left-1/2 -translate-x-1/2 md:left-auto md:right-4 md:translate-x-0 whitespace-nowrap bg-brand-terracotta text-white px-3 py-1 rounded-full font-bold text-xs flex items-center gap-1 shadow-md" data-id="element-33">
                  <Star className="w-3.5 h-3.5 fill-current" data-id="element-34" />
                  {pack.badge}
                </div>}

              <div className="flex items-start gap-4" data-id="element-35">
                {/* Radio indicator */}
                <span className={cn('mt-1 flex-shrink-0 w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors', isSelected ? 'border-brand-terracotta' : 'border-brand-dark/30')} data-id="element-36">
                  <span className={cn('w-3 h-3 rounded-full transition-all', isSelected ? 'bg-brand-terracotta scale-100' : 'scale-0')} data-id="element-37" />
                </span>

                <div className="flex-1 min-w-0" data-id="element-38">
                  <div className="flex items-center gap-2 mb-3" data-id="element-39">
                    {pack.highlighted ? <Trophy className="w-5 h-5 text-brand-terracotta flex-shrink-0" data-id="element-40" /> : <BookOpen className="w-5 h-5 text-brand-purple flex-shrink-0" data-id="element-41" />}
                    <h3 className="font-serif text-lg md:text-xl font-bold text-brand-dark leading-tight" data-id="element-42">
                      {pack.title}
                    </h3>
                  </div>

                  <ul className="space-y-1.5 mb-4" data-id="element-43">
                    {pack.items.map((item, i) => <li key={i} className="flex items-start gap-2 text-sm md:text-base text-brand-dark/80 font-medium" data-id="element-44">
                        {item.startsWith('AUDIO') ? <Headphones className="w-4 h-4 text-brand-terracotta flex-shrink-0 mt-0.5" data-id="element-45" /> : <CheckCircle2 className="w-4 h-4 text-brand-green flex-shrink-0 mt-0.5" data-id="element-46" />}
                        <span className={item.startsWith('AUDIO') ? 'font-bold text-brand-terracotta' : ''} data-id="element-47">
                          {item}
                        </span>
                      </li>)}
                  </ul>

                  <div className="flex items-baseline gap-2 flex-wrap" data-id="element-48">
                    <span className="text-sm text-brand-dark/40 line-through" data-id="element-49">
                      {pack.regularPrice}
                    </span>
                    <span className={cn('font-bold', pack.highlighted ? 'text-2xl md:text-3xl text-brand-terracotta' : 'text-2xl md:text-3xl text-brand-dark')} data-id="element-50">
                      {pack.price}{' '}
                      <span className="text-sm font-semibold text-brand-dark/50" data-id="element-51">
                        ARS
                      </span>
                    </span>
                  </div>
                </div>
              </div>
            </button>;
      })}
      </div>

      <CTAButton text="QUIERO ELEGIRME HOY" href={activePack.checkoutUrl} className="w-full md:w-auto md:px-16 py-5 text-xl" data-id="element-52" />
      <p className="text-brand-dark/60 font-medium mt-4" data-id="element-53">
        Estás eligiendo:{' '}
        <span className="font-bold text-brand-dark" data-id="element-54">{activePack.title}</span> ·{' '}
        {activePack.price} ARS · Pago único
      </p>

      <div className="flex flex-wrap justify-center items-center gap-6 mt-8 text-sm text-brand-dark/60 font-medium" data-id="element-55">
        <span className="flex items-center gap-2" data-id="element-56">
          <ShieldCheck className="w-5 h-5 text-brand-green" data-id="element-57" /> Pago 100% Seguro
        </span>
        <span className="flex items-center gap-2" data-id="element-58">
          <BookOpen className="w-5 h-5 text-brand-purple" data-id="element-59" /> Acceso Inmediato
        </span>
        <span className="flex items-center gap-2" data-id="element-60">
          <Heart className="w-5 h-5 text-brand-terracotta" data-id="element-61" /> Garantía 7 días
        </span>
      </div>
    </div>;
}
export function App() {
  return <div className="font-sans text-brand-dark selection:bg-brand-purple/20" data-id="element-62">
      {/* Top Banner */}
      <div className="bg-brand-purple text-white text-center py-2 px-4 font-medium text-[13px] pt-[1px] pb-[1px] pl-[13px] pr-[13px]" data-id="element-63">
        🔥 Más de 1.500 personas ya empezaron a elegirse a sí mismas
      </div>

      {/* Hero Section */}
      <section className="pt-4 pb-8 px-4 md:pt-20 md:pb-32 overflow-hidden" data-id="element-64">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-8 md:gap-12 items-center" data-id="element-65">
          <motion.div initial="hidden" animate="visible" variants={staggerContainer} className="space-y-3 md:space-y-6" data-id="element-66">
            <motion.div variants={fadeIn} className="inline-flex items-center gap-2 bg-brand-terracotta/10 text-brand-terracotta px-3 py-1 md:px-4 md:py-2 rounded-full text-xs md:text-sm font-bold tracking-wide" data-id="element-67">
              <Heart className="w-4 h-4" data-id="element-68" />
              MI PSICÓLOGA ME DIJO™
            </motion.div>

            <motion.h1 variants={fadeIn} className="font-serif text-2xl md:text-5xl lg:text-6xl font-bold leading-tight text-brand-dark" data-id="element-69">
              110 sesiones para elegirte sin culpas y{' '}
              <span className="text-brand-terracotta italic" data-id="element-70">
                dejar de aguantar tanta mierd*
              </span>
            </motion.h1>

            {/* Mobile-only book image: appears directly below the headline */}
            <motion.div variants={fadeIn} className="md:hidden relative" data-id="element-71">
              <div className="absolute inset-0 bg-brand-purple/5 rounded-full blur-3xl transform scale-110 -z-10" data-id="element-72"></div>
              <img src={BOOK_IMAGE} alt="Libro Mi Psicóloga Me Dijo" className="w-full max-w-[250px] mx-auto rounded-xl shadow-xl" data-id="element-73" />
            </motion.div>

            <motion.p variants={fadeIn} className="md:text-xl text-[13px] text-[#080808]" data-id="element-74">
              Recuperá tu paz mental, aprendé a poner límites y dejá de vivir
              para los demás. El libro más honesto, crudo y transformador para
              quienes están cansados de cargar culpas ajenas.
            </motion.p>

            <motion.div variants={fadeIn} className="flex items-center text-brand-dark/70 gap-[3px]" data-id="element-75">
              <div className="flex text-yellow-500" data-id="element-76">
                {[...Array(5)].map((_, i) => <Star key={i} className="w-5 h-5 fill-current" data-id="element-77" />)}
              </div>
              <span className="font-medium" data-id="element-78">
                4.9/5 Calificación de lectores
              </span>
            </motion.div>

            <motion.ul variants={staggerContainer} className="pt-1 md:pt-4 space-y-0 md:space-y-[2px]" data-id="element-79">
              {['Dejá de sentir culpa por priorizarte', 'Aprendé a poner límites sin miedo', 'Soltá relaciones que te desgastan', 'Elegite sin pedir permiso', 'Acceso digital inmediato'].map((item, i) => <motion.li key={i} variants={fadeIn} className="flex items-start md:gap-3 gap-[3px]" data-id="element-80">
                  <CheckCircle2 className="w-4 h-4 md:w-6 md:h-6 text-brand-green flex-shrink-0 mt-0.5 md:mt-0" data-id="element-81" />
                  <span className="font-medium text-[13px] md:text-base" data-id="element-82">
                    {item}
                  </span>
                </motion.li>)}
            </motion.ul>

            <motion.div variants={fadeIn} className="pt-2 md:pt-6 space-y-3 md:space-y-4" data-id="element-83">
              <CTAButton text="QUIERO ELEGIRME HOY" data-id="element-84" />
              <div className="flex items-center gap-4 text-sm text-brand-dark/60 font-medium" data-id="element-85">
                <span className="flex items-center gap-1" data-id="element-86">
                  <ShieldCheck className="w-4 h-4" data-id="element-87" /> Pago seguro
                </span>
                <span className="flex items-center gap-1" data-id="element-88">
                  <Clock className="w-4 h-4" data-id="element-89" /> Garantía 7 días
                </span>
              </div>
            </motion.div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          x: 50
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.8
        }} className="relative hidden md:block" data-id="element-90">
            <div className="absolute inset-0 bg-brand-purple/5 rounded-full blur-3xl transform scale-110 -z-10" data-id="element-91"></div>
            <img src={BOOK_IMAGE} alt="Libro Mi Psicóloga Me Dijo" className="w-full max-w-md mx-auto rounded-2xl shadow-2xl transform -rotate-2 hover:rotate-0 transition-transform duration-500" data-id="element-92" />
          </motion.div>
        </div>
      </section>

      {/* Pain Points Section */}
      <section className="py-12 md:py-20 bg-brand-light px-4" data-id="element-93">
        <div className="max-w-4xl mx-auto text-center space-y-8 md:space-y-12" data-id="element-94">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeIn} className="space-y-6" data-id="element-95">
            <h2 className="font-serif text-3xl md:text-5xl font-bold text-brand-dark" data-id="element-96">
              ¿CUÁNTO TIEMPO MÁS VAS A SEGUIR AGUANTANDO COSAS QUE TE HACEN MAL?
            </h2>
            <div className="text-xl md:text-2xl text-brand-terracotta font-medium space-y-2" data-id="element-97">
              <p data-id="element-98">Te cansaste.</p>
              <p data-id="element-99">Te cansaste de ser fuerte para todos.</p>
              <p data-id="element-100">Te cansaste de callarte para evitar conflictos.</p>
              <p data-id="element-101">Te cansaste de dar más de lo que recibís.</p>
              <p data-id="element-102">Te cansaste de sentir culpa cada vez que pensás en vos.</p>
            </div>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-4 md:gap-8 text-left pt-2 md:pt-8" data-id="element-103">
            {[{
            title: 'Vivís complaciendo a los demás',
            desc: 'Decís que sí cuando querés decir no.'
          }, {
            title: 'Te sentís responsable de todo',
            desc: 'Cargás problemas que ni siquiera son tuyos.'
          }, {
            title: 'Te cuesta poner límites',
            desc: 'Aunque estés agotada, seguís dando.'
          }, {
            title: 'Permanecés donde ya no sos feliz',
            desc: 'Por miedo a decepcionar o lastimar a otros.'
          }, {
            title: 'Te tragás todo lo que sentís',
            desc: 'Hasta que explotás emocionalmente.'
          }, {
            title: 'Te olvidaste de vos',
            desc: 'Porque siempre hay alguien más primero.'
          }].map((item, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.1
          }} className="bg-white p-6 rounded-2xl shadow-sm border border-brand-dark/5" data-id="element-104">
                <div className="flex items-start gap-4" data-id="element-105">
                  <XCircle className="w-6 h-6 text-brand-terracotta flex-shrink-0 mt-1" data-id="element-106" />
                  <div data-id="element-107">
                    <h3 className="font-bold text-lg text-brand-dark mb-1" data-id="element-108">
                      {item.title}
                    </h3>
                    <p className="text-brand-dark/70" data-id="element-109">{item.desc}</p>
                  </div>
                </div>
              </motion.div>)}
          </div>

          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeIn} className="bg-brand-purple text-white p-6 md:p-12 rounded-3xl mt-8 md:mt-12 shadow-xl" data-id="element-110">
            <p className="text-xl md:text-2xl font-serif italic mb-6" data-id="element-111">
              "No es porque seas débil. No es porque no tengas voluntad. Es
              porque nadie te enseñó a elegirte sin sentir culpa."
            </p>
            <CTAButton text="QUIERO CAMBIAR ESTO" className="bg-white text-brand-purple hover:bg-brand-light hover:text-brand-purple" data-id="element-112" />
          </motion.div>
        </div>
      </section>

      {/* Image Carousel */}
      <ImageCarousel data-id="element-113" />

      {/* Solution Section */}
      <section className="py-12 md:py-20 px-4 pt-[41px] pb-[41px] space-y-[30px]" data-id="element-114">
        <div className="max-w-4xl mx-auto text-center space-y-8" data-id="element-115">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeIn} data-id="element-116">
            <h2 className="font-serif text-3xl md:text-5xl font-bold text-brand-dark mb-6" data-id="element-117">
              Este no es un libro para quien quiere seguir fingiendo que puede
              con todo.
            </h2>
            <div className="space-y-4 text-lg md:text-xl text-brand-dark/80" data-id="element-118">
              <p data-id="element-119">Es para quien ya no puede más.</p>
              <p data-id="element-120">Para quien se hartó de ser fuerte todo el tiempo.</p>
              <p data-id="element-121">Para quien está cansado de tragarse lo que siente.</p>
              <p data-id="element-122">
                Para quien necesita escuchar las verdades incómodas que cambian
                una vida.
              </p>
            </div>

            <div className="mt-8 md:mt-12 p-6 md:p-8 border-2 border-brand-green/20 rounded-3xl bg-brand-green/5" data-id="element-123">
              <p className="font-serif text-2xl text-brand-green italic mb-4" data-id="element-124">
                Nace de conversaciones reales.
              </p>
              <p className="text-lg text-brand-dark/80" data-id="element-125">
                De esas sesiones donde una terapeuta te dice exactamente lo que
                necesitabas escuchar.
                <br data-id="element-126" />
                <span className="font-bold mt-4 block" data-id="element-127">
                  Sin filtros. Sin frases vacías. Sin humo. Solo verdad.
                </span>
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-12 md:py-20 bg-brand-dark text-brand-cream px-4" data-id="element-128">
        <div className="max-w-5xl mx-auto" data-id="element-129">
          <div className="text-center mb-10 md:mb-16" data-id="element-130">
            <h2 className="font-serif text-3xl md:text-5xl font-bold mb-4" data-id="element-131">
              LO QUE VAS A LOGRAR DENTRO DEL LIBRO
            </h2>
            <div className="w-24 h-1 bg-brand-terracotta mx-auto rounded-full" data-id="element-132"></div>
          </div>

          <div className="grid md:grid-cols-2 gap-4 md:gap-8" data-id="element-133">
            {[{
            icon: '💭',
            title: 'Soltar culpas que no te pertenecen',
            desc: 'Aprendé a dejar de cargar responsabilidades ajenas.'
          }, {
            icon: '🚫',
            title: 'Poner límites sin sentirte una mala persona',
            desc: 'Decí NO sin justificarte.'
          }, {
            icon: '❤️',
            title: 'Elegirte sin egoísmo',
            desc: 'Descubrí por qué priorizarte es una necesidad.'
          }, {
            icon: '😔',
            title: 'Dejar de soportar relaciones que te destruyen',
            desc: 'Identificá vínculos que te drenan emocionalmente.'
          }, {
            icon: '🧠',
            title: 'Entender lo que realmente sentís',
            desc: 'Reconectate con tus emociones.'
          }, {
            icon: '🌱',
            title: 'Recuperar tu autoestima',
            desc: 'Volvé a confiar en vos y en tus decisiones.'
          }, {
            icon: '✨',
            title: 'Dejar de vivir para agradar a los demás',
            desc: 'Empezá a construir una vida alineada con lo que querés.'
          }].map((item, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.1
          }} className="flex gap-4 p-6 rounded-2xl bg-white/5 border border-white/10 hover:bg-white/10 transition-colors" data-id="element-134">
                <div className="text-4xl" data-id="element-135">{item.icon}</div>
                <div data-id="element-136">
                  <h3 className="font-bold text-xl mb-2 text-brand-terracotta" data-id="element-137">
                    {item.title}
                  </h3>
                  <p className="text-brand-cream/80" data-id="element-138">{item.desc}</p>
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Bonuses Section */}
      <section className="py-12 md:py-20 px-4 bg-brand-light" data-id="element-139">
        <div className="max-w-5xl mx-auto" data-id="element-140">
          <div className="text-center mb-10 md:mb-16" data-id="element-141">
            <div className="inline-flex items-center justify-center p-4 bg-brand-terracotta/10 rounded-full mb-6 text-brand-terracotta" data-id="element-142">
              <Gift className="w-12 h-12" data-id="element-143" />
            </div>
            <h2 className="font-serif text-3xl md:text-5xl font-bold text-brand-dark mb-4" data-id="element-144">
              4 BONOS ESTRATÉGICOS INCLUIDOS GRATIS HOY
            </h2>
            <p className="text-xl text-brand-dark/70" data-id="element-145">
              Valor total de los bonos:{' '}
              <span className="line-through" data-id="element-146">$52.000</span>{' '}
              <span className="font-bold text-brand-green" data-id="element-147">GRATIS HOY</span>
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-4 md:gap-8" data-id="element-148">
            {[{
            num: 1,
            title: 'Diario de Transformación Personal',
            desc: 'Workbook imprimible de 21 días. Reflexiones, ejercicios y espacios para escribir y reconectar con vos.',
            value: '$12.000'
          }, {
            num: 2,
            title: 'El Poder de Dejar Ir',
            desc: 'Rompe con Relaciones Tóxicas. Aprendé a identificar vínculos dañinos y recuperar tu bienestar emocional.',
            value: '$15.000'
          }, {
            num: 3,
            title: 'Primero Yo, Después el Resto',
            desc: '120 sesiones de autoconocimiento y empoderamiento personal.',
            value: '$10.000'
          }, {
            num: 4,
            title: 'Cómo Cambiar Tu Vida en 50 Hojas',
            desc: 'Guía práctica para fortalecer autoestima, amor propio y aceptación.',
            value: '$15.000'
          }].map((bono, i) => <motion.div key={i} initial={{
            opacity: 0,
            scale: 0.95
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true
          }} className="bg-white p-6 md:p-8 rounded-3xl shadow-lg border-t-4 border-brand-terracotta relative overflow-hidden" data-id="element-149">
                <div className="absolute top-0 right-0 bg-brand-terracotta text-white px-4 py-1 rounded-bl-xl font-bold text-sm" data-id="element-150">
                  GRATIS
                </div>
                <div className="text-brand-terracotta font-bold mb-2" data-id="element-151">
                  BONUS {bono.num}
                </div>
                <h3 className="font-serif text-2xl font-bold text-brand-dark mb-3" data-id="element-152">
                  {bono.title}
                </h3>
                <p className="text-brand-dark/70 mb-4" data-id="element-153">{bono.desc}</p>
                <div className="text-sm text-brand-dark/50 font-medium" data-id="element-154">
                  Valor normal:{' '}
                  <span className="line-through" data-id="element-155">{bono.value}</span>
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Offer Section */}
      <section id="oferta" className="py-12 md:py-20 px-4" data-id="element-156">
        <div className="max-w-4xl mx-auto" data-id="element-157">
          <div className="bg-white rounded-3xl md:rounded-[3rem] shadow-2xl overflow-hidden border-4 border-brand-terracotta/20" data-id="element-158">
            <div className="bg-brand-terracotta text-white text-center py-4 font-bold tracking-widest uppercase flex items-center justify-center gap-2" data-id="element-159">
              <Sparkles className="w-5 h-5" data-id="element-160" />
              Oferta Especial de Lanzamiento
              <Sparkles className="w-5 h-5" data-id="element-161" />
            </div>

            <div className="p-6 md:p-12 text-center" data-id="element-162">
              <div className="mb-8" data-id="element-163">
                <p className="text-brand-dark/60 font-bold mb-2 flex items-center justify-center gap-2" data-id="element-164">
                  <Clock className="w-5 h-5" data-id="element-165" /> ESTA OFERTA TERMINA EN:
                </p>
                <CountdownTimer data-id="element-166" />
                <p className="text-sm text-brand-terracotta font-medium" data-id="element-167">
                  ⚠️ Luego vuelve a precio normal.
                </p>
              </div>

              <OfferSelector data-id="element-168" />
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-12 md:py-20 bg-brand-light px-4" data-id="element-169">
        <div className="max-w-6xl mx-auto" data-id="element-170">
          <div className="text-center mb-10 md:mb-16" data-id="element-171">
            <h2 className="font-serif text-3xl md:text-5xl font-bold text-brand-dark mb-4" data-id="element-172">
              PERSONAS REALES QUE YA LEYERON EL LIBRO
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-173">
            {[{
            name: 'Mariana G.',
            loc: 'Córdoba',
            text: 'Sentía culpa por todo. Después de leer este libro entendí que estaba viviendo para los demás. Hoy pongo límites sin sentirme mal.'
          }, {
            name: 'Carla M.',
            loc: 'Buenos Aires',
            text: 'Cada página parece una sesión de terapia. Me hizo enfrentar cosas que venía evitando hace años.'
          }, {
            name: 'Luciana P.',
            loc: 'Rosario',
            text: 'Me ayudó a salir de una relación donde estaba soportando demasiado por miedo a quedarme sola.'
          }, {
            name: 'Andrea R.',
            loc: 'Mendoza',
            text: 'Es brutalmente honesto. Te sacude, pero también te ayuda a sanar.'
          }, {
            name: 'Sofía C.',
            loc: 'La Plata',
            text: 'Por primera vez entendí que elegirme no es egoísmo. Es supervivencia.'
          }].map((test, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.1
          }} className="bg-white p-6 md:p-8 rounded-2xl shadow-sm border border-brand-dark/5" data-id="element-174">
                <div className="flex text-yellow-500 mb-4" data-id="element-175">
                  {[...Array(5)].map((_, j) => <Star key={j} className="w-4 h-4 fill-current" data-id="element-176" />)}
                </div>
                <p className="text-brand-dark/80 italic mb-6" data-id="element-177">"{test.text}"</p>
                <div data-id="element-178">
                  <p className="font-bold text-brand-dark" data-id="element-179">{test.name}</p>
                  <p className="text-sm text-brand-dark/50" data-id="element-180">{test.loc}</p>
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Guarantee Section */}
      <section className="py-12 md:py-20 px-4" data-id="element-181">
        <div className="max-w-3xl mx-auto bg-brand-green text-white rounded-3xl p-6 md:p-12 text-center shadow-xl" data-id="element-182">
          <ShieldCheck className="w-16 h-16 md:w-20 md:h-20 mx-auto mb-6 text-brand-cream/80" data-id="element-183" />
          <h2 className="font-serif text-3xl md:text-4xl font-bold mb-6" data-id="element-184">
            GARANTÍA DE 7 DÍAS
          </h2>
          <div className="text-lg md:text-xl space-y-4 text-brand-cream/90 mb-8" data-id="element-185">
            <p data-id="element-186">Descargá el libro.</p>
            <p data-id="element-187">Leelo.</p>
            <p data-id="element-188">Aplicá los ejercicios.</p>
            <p className="font-bold text-white mt-6" data-id="element-189">
              Y si sentís que no era para vos, te devolvemos el 100% de tu
              dinero.
            </p>
          </div>
          <div className="flex justify-center gap-4 text-sm font-medium text-brand-cream/70" data-id="element-190">
            <span data-id="element-191">Sin preguntas.</span>
            <span data-id="element-192">•</span>
            <span data-id="element-193">Sin vueltas.</span>
            <span data-id="element-194">•</span>
            <span data-id="element-195">Sin riesgo para vos.</span>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-12 md:py-20 bg-brand-light px-4" data-id="element-196">
        <div className="max-w-3xl mx-auto" data-id="element-197">
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-center text-brand-dark mb-8 md:mb-12" data-id="element-198">
            PREGUNTAS FRECUENTES
          </h2>
          <div className="bg-white rounded-3xl p-6 md:p-10 shadow-sm border border-brand-dark/5" data-id="element-199">
            <FAQItem question="¿Es un libro o una terapia?" answer="Es un libro de desarrollo personal basado en reflexiones profundas y conversaciones terapéuticas reales." data-id="element-200" />
            <FAQItem question="¿Cómo recibo el material?" answer="Lo recibís por correo electrónico inmediatamente después de la compra. Es formato digital (PDF) para que puedas leerlo en cualquier dispositivo." data-id="element-201" />
            <FAQItem question="¿Qué métodos de pago aceptan?" answer="Tarjeta de crédito, débito, Mercado Pago y transferencias según disponibilidad en tu país." data-id="element-202" />
            <FAQItem question="¿Cuándo tendré acceso?" answer="Inmediatamente después de confirmar el pago. El sistema te envía el acceso de forma automática 24/7." data-id="element-203" />
            <FAQItem question="¿Necesito conocimientos previos?" answer="No. Está escrito para cualquier persona que quiera mejorar su bienestar emocional, con un lenguaje claro, directo y sin tecnicismos." data-id="element-204" />
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-14 md:py-24 px-4 bg-brand-dark text-brand-cream text-center" data-id="element-205">
        <div className="max-w-4xl mx-auto" data-id="element-206">
          <h2 className="font-serif text-3xl md:text-5xl font-bold mb-8 md:mb-12" data-id="element-207">
            ⚖️ ¿CUÁNTO CUESTA NO EMPEZAR HOY?
          </h2>

          <div className="space-y-4 text-lg md:text-xl text-left max-w-2xl mx-auto mb-10 md:mb-16" data-id="element-208">
            {['Seguir soportando situaciones que te lastiman.', 'Seguir sintiéndote culpable por pensar en vos.', 'Seguir priorizando a todo el mundo menos a vos.', 'Seguir callando lo que sentís.', 'Seguir viviendo una vida que ya no querés vivir.'].map((item, i) => <div key={i} className="flex items-start gap-4" data-id="element-209">
                <XCircle className="w-6 h-6 text-brand-terracotta flex-shrink-0 mt-1" data-id="element-210" />
                <span className="text-brand-cream/80" data-id="element-211">{item}</span>
              </div>)}
          </div>

          <div className="text-2xl md:text-3xl font-serif italic mb-8 md:mb-12 text-brand-terracotta" data-id="element-212">
            <p data-id="element-213">El mejor momento para elegirte era ayer.</p>
            <p className="font-bold mt-2" data-id="element-214">El segundo mejor momento es hoy.</p>
          </div>

          <CTAButton text="QUIERO ELEGIRME HOY" className="w-full md:w-auto md:px-16 py-5 text-xl" data-id="element-215" />
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#1a1816] text-brand-cream/50 py-8 text-center text-sm" data-id="element-216">
        <p data-id="element-217">© 2026 Mi Psicóloga Me Dijo™. Todos los derechos reservados.</p>
        <p className="mt-2" data-id="element-218">
          Este sitio no es parte del sitio web de Facebook o Facebook Inc.
          Además, este sitio NO está respaldado por Facebook de ninguna manera.
        </p>
      </footer>
    </div>;
}